import React from 'react'
import PropTypes from 'prop-types'
import goPrevStep from '@utils/goPrevStep'
import { useFormContext } from '@context/FormContext'

const nextLabels = {
  0: 'Next: Eligibility →',
  1: 'Next: Contact Info →',
  2: 'Next: Schedule →',
  3: 'Confirm Appointment →',
}

/** Animated spinner shown inside buttons while a network request is in flight. */
const SpinnerIcon = () => (
  <svg className="animate-spin" style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
)

const Footer = ({
  currentStep,
  currentStepIndex,
  isSubmitting,
  setCurrentStepIndex,
  stepsLength,
  enableSubmitButton,
  values: valuesProp,
  hasErrors = false,
}) => {
  const { values: valuesFromContext, getCurrentValues, setFieldValue } = useFormContext()
  const values = valuesFromContext ?? valuesProp ?? {}
  const latestValues = typeof getCurrentValues === 'function' ? getCurrentValues() : values
  const v = latestValues || {}

  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === stepsLength - 1
  const isSubmitStep = currentStep?.info?.isSubmitStep
  // Only the Schedule step books a slot. Cohorts that submit from ContactInfo
  // (HVAC Service, CAP HEA, paused-gas HEA leads) are not confirming an appointment.
  const isSchedulingSubmit = currentStepIndex === stepsLength - 2

  // HealthyHome users land directly on Eligibility (step 1) — there is no ChoosePath to go back to
  const isHealthyHomeEligibility = currentStepIndex === 1 && String(v.path ?? '').toLowerCase() === 'healthyhome'

  // Block Next button until howHeated is answered for HVAC sales eligibility
  const isEligibilityStep = currentStepIndex === 1
  const isHvacPath = String(v.path ?? '').trim().toLowerCase() === 'hvac'
  const fuelAssistanceIsNo = v.fuelAssistance === 'no'
  const familySizeStr = v.familySize != null ? String(v.familySize).trim() : ''
  const howHeatedQuestionVisible =
    fuelAssistanceIsNo && familySizeStr !== '' && ['1', '1-4'].includes(familySizeStr)
  const howHeatedSelected =
    v.howHeated != null && v.howHeated !== '' && String(v.howHeated).trim() !== ''
  const blockNextUntilHowHeated =
    isEligibilityStep && isHvacPath && howHeatedQuestionVisible && !howHeatedSelected

  const nextStepDisabled = isSubmitting || hasErrors || blockNextUntilHowHeated
  const nextLabel = nextLabels[currentStepIndex] || 'Next →'

  if (isLastStep) {
    return (
      <footer
        className="sticky bottom-0 bg-white border-t border-stone-200 z-40"
        style={{ boxShadow: '0 -2px 12px rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center gap-3">
          <a
            href="https://homeworksenergy.com"
            className="inline-flex items-center gap-2 font-semibold text-base px-6 py-3 rounded-xl text-white transition-all"
            style={{ background: '#4675ad', minHeight: 48, textDecoration: 'none' }}
          >
            ← Return to HomeWorks Energy
          </a>
        </div>
      </footer>
    )
  }

  return (
    <footer
      className="sticky bottom-0 bg-white border-t border-stone-200 z-40"
      style={{ boxShadow: '0 -2px 12px rgba(0,0,0,0.06)' }}
    >
      <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">

        {/* Back button — also hidden when HealthyHome user is on eligibility (no prior step) */}
        {!isFirstStep && !isHealthyHomeEligibility && (
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => goPrevStep(setCurrentStepIndex, setFieldValue)}
            className="flex-shrink-0 flex items-center gap-2 font-semibold text-sm px-5 py-3 rounded-xl border-2 border-stone-200 text-stone-600 bg-white hover:bg-stone-50 hover:border-stone-300 transition-all"
            style={{ minHeight: 48 }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M9 2L4 7l5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
        )}

        <div className="flex-1" />

        {/* Helper text when Next is disabled */}
        {nextStepDisabled && !isSubmitting && (
          <div className="text-xs text-stone-400 text-right max-w-[140px] leading-tight hidden sm:block">
            Answer all questions above to continue
          </div>
        )}

        {/* Next Step button */}
        {!isSubmitStep && (
          <button
            type="submit"
            disabled={nextStepDisabled}
            className="flex-shrink-0 flex items-center gap-2 font-semibold text-base px-6 py-3 rounded-xl text-white transition-all"
            style={{
              minHeight: 48,
              background: '#99c543',
              opacity: nextStepDisabled ? 0.4 : 1,
              cursor: nextStepDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon />
                Processing...
              </>
            ) : (
              <span>{nextLabel}</span>
            )}
          </button>
        )}

        {/* Confirm Appointment button (submit steps only) */}
        {isSubmitStep && (
          <button
            type="submit"
            disabled={!enableSubmitButton || isSubmitting}
            className="flex-shrink-0 flex items-center gap-2 font-semibold text-base px-6 py-3 rounded-xl text-white transition-all"
            style={{
              minHeight: 48,
              background: '#99c543',
              opacity: !enableSubmitButton || isSubmitting ? 0.4 : 1,
              cursor: !enableSubmitButton || isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon />
                Submitting...
              </>
            ) : isSchedulingSubmit ? (
              'Confirm Appointment →'
            ) : (
              'Submit My Information →'
            )}
          </button>
        )}
      </div>
    </footer>
  )
}

Footer.propTypes = {
  currentStep: PropTypes.func.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setCurrentStepIndex: PropTypes.func.isRequired,
  stepsLength: PropTypes.number.isRequired,
  enableSubmitButton: PropTypes.bool.isRequired,
  values: PropTypes.shape({ schedule: PropTypes.string }).isRequired,
  hasErrors: PropTypes.bool,
}

export default Footer
